import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'opanuj-to:enrollment:v1';
export type EnrollmentGuide = 'next-step' | 'selected-lessons' | null;

export type PersistedEnrollmentState = {
    lessonsList: any[];
    notes: string;
    client: string;
};

export type EnrollmentState = PersistedEnrollmentState & {
    step: number;
    statuteAccepted: boolean;
    isBasketOpen: boolean;
};

export type EnrollmentStepValidation = {
    isComplete: boolean;
    issue: string | null;
};

const defaultState: PersistedEnrollmentState = {
    lessonsList: [],
    notes: '',
    client: '',
};

const defaultTransientState = {
    step: 0,
    statuteAccepted: false,
    isBasketOpen: false,
    enrollmentGuide: null as EnrollmentGuide,
};

export const getEnrollmentStepValidation = ({
    step,
    lessonsList,
    user,
    userLoading,
    statuteAccepted,
}: {
    step: number;
    lessonsList: any[];
    user: any;
    userLoading: boolean;
    statuteAccepted: boolean;
}): EnrollmentStepValidation => {
    switch (step) {
        case 0:
            return {
                isComplete: lessonsList.length > 0,
                issue: lessonsList.length > 0 ? null : 'Wybierz co najmniej 1 zajęcia',
            };
        case 1:
            if (lessonsList.length === 0) return { isComplete: false, issue: 'Wybierz co najmniej 1 zajęcia' };
            if (userLoading) return { isComplete: false, issue: null };
            if (!user) return { isComplete: false, issue: 'Zaloguj się lub załóż konto aby kontynuować' };
            if (!user.email) return { isComplete: false, issue: 'Podaj poprawny email' };
            return { isComplete: true, issue: null };
        case 2:
            if (lessonsList.length === 0) return { isComplete: false, issue: 'Lista zajęć nie może być pusta' };
            if (!statuteAccepted) return { isComplete: false, issue: 'Aby kontynuować zaakceptuj regulamin(y) zajęć' };
            return { isComplete: true, issue: null };
        default:
            return { isComplete: true, issue: null };
    }
};

type EnrollmentContextValue = PersistedEnrollmentState & typeof defaultTransientState & {
    pickedLesson: any;
    setStep: (step: number) => void;
    goToNextStep: () => void;
    goToPrevStep: () => void;
    setLessonsList: React.Dispatch<React.SetStateAction<any[]>>;
    setNotes: (notes: string) => void;
    setClient: (client: string) => void;
    setStatuteAccepted: (accepted: boolean) => void;
    setPickedLesson: (lesson: any) => void;
    clearPersistedState: () => void;
    openBasket: () => void;
    closeBasket: () => void;
    toggleBasket: () => void;
    showNextStepGuide: () => void;
    showSelectedLessonsGuide: () => void;
    hideEnrollmentGuide: () => void;
};

const readPersistedState = (): PersistedEnrollmentState => {
    if (typeof window === 'undefined') return defaultState;

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultState;

        const parsed = JSON.parse(raw) as Partial<PersistedEnrollmentState>;

        return {
            step: typeof parsed.step === 'number' ? parsed.step : defaultState.step,
            lessonsList: Array.isArray(parsed.lessonsList) ? parsed.lessonsList : defaultState.lessonsList,
            notes: typeof parsed.notes === 'string' ? parsed.notes : defaultState.notes,
            client: typeof parsed.client === 'string' ? parsed.client : defaultState.client,
        };
    } catch {
        return defaultState;
    }
};

const EnrollmentContext = createContext<EnrollmentContextValue | null>(null);

export function EnrollmentProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const [state, setState] = useState<PersistedEnrollmentState>(() => readPersistedState());
    const [statuteAccepted, setStatuteAcceptedState] = useState<boolean>(false);
    const [step, setStep] = useState<number>(0);
    const [isBasketOpen, setIsBasketOpen] = useState<boolean>(false);
    const [enrollmentGuide, setEnrollmentGuide] = useState<EnrollmentGuide>(null);
    const [pickedLesson, setPickedLesson] = useState<any>({});

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const isEmptyState = state.lessonsList.length === 0 && !state.notes && !state.client;

        if (isEmptyState) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !location.pathname.startsWith('/zapisy') ||
            state.lessonsList.length === 0
        ) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = 'Wybrane przez Ciebie terminy, nie zostały jeszcze wysłane. Przejdź dalej, aby je wysłać.';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [location.pathname, state.lessonsList.length]);

    const clearPersistedState = useCallback(() => {
        setState(defaultState);
        setStatuteAcceptedState(false);
        setIsBasketOpen(false);
        setEnrollmentGuide(null);
        setPickedLesson({});
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const value = useMemo<EnrollmentContextValue>(() => ({
        ...state,
        ...defaultTransientState,
        statuteAccepted,
        isBasketOpen,
        enrollmentGuide,
        step,
        pickedLesson,
        setStep,
        goToNextStep: () => setStep((previous) => previous + 1),
        goToPrevStep: () => setStep((previous) => Math.max(0, previous - 1)),
        setLessonsList: (update) => setState((previous) => ({
            ...previous,
            lessonsList: typeof update === 'function' ? update(previous.lessonsList) : update,
        })),
        setNotes: (notes: string) => setState((previous) => ({ ...previous, notes })),
        setClient: (client: string) => setState((previous) => ({ ...previous, client })),
        setStatuteAccepted: (accepted: boolean) => setStatuteAcceptedState(accepted),
        setPickedLesson,
        clearPersistedState,
        openBasket: () => setIsBasketOpen(true),
        closeBasket: () => setIsBasketOpen(false),
        toggleBasket: () => setIsBasketOpen((previous) => !previous),
        showNextStepGuide: () => setEnrollmentGuide('next-step'),
        showSelectedLessonsGuide: () => setEnrollmentGuide('selected-lessons'),
        hideEnrollmentGuide: () => setEnrollmentGuide(null),
    }), [clearPersistedState, enrollmentGuide, isBasketOpen, pickedLesson, state, statuteAccepted, step]);

    return <EnrollmentContext.Provider value={value}>{children}</EnrollmentContext.Provider>;
}

export function useEnrollment() {
    const context = useContext(EnrollmentContext);

    if (!context) {
        throw new Error('useEnrollment must be used within EnrollmentProvider');
    }

    return context;
}
